/**
 * User activity tracking: page views, time on page, and clicks.
 * Logs are stored in Firestore collection "userActivityLogs".
 */
import { getAuth } from 'firebase/auth';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';

const COLLECTION = 'userActivityLogs';
const CLICK_THROTTLE_MS = 2000;

let lastClickLogTime = 0;

function getCurrentUser() {
  const auth = getAuth();
  return auth.currentUser;
}

/**
 * Log a page view.
 * @param {string} pathname - Current pathname (e.g. /rsvp)
 */
export async function logPageView(pathname) {
  const user = getCurrentUser();
  if (!user) return;
  try {
    const ref = collection(db, COLLECTION);
    await addDoc(ref, {
      userId: user.uid,
      phoneNumber: user.phoneNumber || '',
      eventType: 'page_view',
      pathname: pathname || window.location.pathname,
      timestamp: serverTimestamp(),
    });
  } catch (err) {
    console.warn('Tracking logPageView failed:', err);
  }
}

/**
 * Log page exit with time spent on page.
 * @param {string} pathname - Path that was left
 * @param {number} durationSeconds - Seconds spent on the page
 */
export async function logPageExit(pathname, durationSeconds) {
  const user = getCurrentUser();
  if (!user) return;
  try {
    const ref = collection(db, COLLECTION);
    await addDoc(ref, {
      userId: user.uid,
      phoneNumber: user.phoneNumber || '',
      eventType: 'page_exit',
      pathname: pathname || '',
      durationSeconds: Math.round(durationSeconds),
      timestamp: serverTimestamp(),
    });
  } catch (err) {
    console.warn('Tracking logPageExit failed:', err);
  }
}

/**
 * Build a short description of the clicked element (tag, id, text slice).
 * Only includes defined values (Firestore does not allow undefined).
 * @param {Event} e - Click event
 * @returns {object} - { tagName, id?, className?, text? } with no undefined values
 */
function getClickTarget(e) {
  const el = e.target;
  if (!el || !el.tagName) return { tagName: 'unknown' };
  const text = (el.textContent || '').trim().slice(0, 80);
  const raw = {
    tagName: (el.tagName || '').toLowerCase(),
    id: el.id || null,
    className: (el.className && typeof el.className === 'string') ? el.className.slice(0, 100) : null,
    text: text || null,
  };
  return Object.fromEntries(Object.entries(raw).filter(([, v]) => v != null));
}

/**
 * Log a click (throttled to avoid flooding).
 * @param {string} pathname - Current pathname
 * @param {object} clickTarget - { tagName, id?, className?, text? }
 */
export async function logClick(pathname, clickTarget) {
  const user = getCurrentUser();
  if (!user) return;
  const now = Date.now();
  if (now - lastClickLogTime < CLICK_THROTTLE_MS) return;
  lastClickLogTime = now;
  try {
    const ref = collection(db, COLLECTION);
    const payload = {
      userId: user.uid,
      phoneNumber: user.phoneNumber || '',
      eventType: 'click',
      pathname: pathname || window.location.pathname,
      clickTarget: clickTarget && typeof clickTarget === 'object'
        ? Object.fromEntries(Object.entries(clickTarget).filter(([, v]) => v !== undefined))
        : {},
      timestamp: serverTimestamp(),
    };
    await addDoc(ref, payload);
  } catch (err) {
    console.warn('Tracking logClick failed:', err);
  }
}

/**
 * Attach global click listener that logs clicks (throttled).
 * Call once from App (e.g. in useEffect).
 * @returns {() => void} - Cleanup function
 */
export function attachClickTracker() {
  const handler = (e) => {
    const pathname = window.location.pathname;
    const clickTarget = getClickTarget(e);
    logClick(pathname, clickTarget);
  };
  window.addEventListener('click', handler, true);
  return () => window.removeEventListener('click', handler, true);
}
